import { useEffect, useState } from 'react'
import { callCenterSetupApi } from '../api/callCenterSetup'
import { userDirectoryApi, type DirectoryUser } from '../api/userDirectory'
import { Button, Checkbox } from './ui'

/**
 * F-088 (TASK-0342): gli operatori della lista (campagna). Solo informativo: non cambia chi vede i lead (la coda
 * resta comune) né l'instradamento. Lettura e scrittura sono riservate agli Admin: se il server risponde 403 la
 * sezione non compare.
 */
export function CampaignOperatorsField({ campaignId }: { campaignId: number }) {
  const [allowed, setAllowed] = useState(false)
  const [users, setUsers] = useState<DirectoryUser[]>([])
  const [selected, setSelected] = useState<number[]>([])
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')

  useEffect(() => {
    let alive = true
    setAllowed(false)
    setMessage('')
    Promise.all([
      callCenterSetupApi.campaignOperators.get(campaignId),
      userDirectoryApi.list({ limit: 200 }),
    ])
      .then(([ops, page]) => {
        if (!alive) return
        setSelected(ops.userIds)
        setUsers(page)
        setAllowed(true)
      })
      .catch(() => { if (alive) setAllowed(false) })
    return () => { alive = false }
  }, [campaignId])

  if (!allowed) return null

  function toggle(id: number) {
    setSelected(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]))
  }

  async function save() {
    setSaving(true)
    setMessage('')
    try {
      await callCenterSetupApi.campaignOperators.replace(campaignId, selected)
      setMessage('Operatori salvati.')
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Errore durante il salvataggio')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="md-span-full d-flex flex-column gap-2">
      <div className="md-typescale-label-large">Operatori della lista</div>
      <div className="md-u-scroll-y-180 md-u-list-box">
        {users.length === 0 && <div className="text-muted md-typescale-body-small">Nessun utente attivo</div>}
        {users.map(u => (
          <Checkbox key={u.id} id={`co-${u.id}`} label={u.name}
            checked={selected.includes(u.id)} onChange={() => toggle(u.id)} />
        ))}
        {selected.filter(id => !users.some(u => u.id === id)).map(id => (
          <Checkbox key={id} id={`co-${id}`} label={`Utente #${id}`} checked onChange={() => toggle(id)} />
        ))}
      </div>
      <div className="d-flex align-items-center gap-2">
        <Button type="button" variant="outlined" size="sm" disabled={saving} onClick={() => void save()}>
          {saving ? 'Salvataggio...' : 'Salva operatori'}
        </Button>
        {message && <span className="md-typescale-body-small text-muted">{message}</span>}
      </div>
      <div className="md-typescale-body-small text-muted">
        Solo informativo: gli operatori della lista non limitano chi vede i lead (la coda è comune) e non cambiano
        l'assegnazione. Il salvataggio degli operatori è indipendente dal pulsante Salva della campagna.
      </div>
    </div>
  )
}
